import { __decorate } from "tslib";
import React, { cloneElement, Component, isValidElement } from 'react';
import { observer } from 'mobx-react';
import { action, isArrayLike, observable, runInAction, toJS } from 'mobx';
import uniq from 'lodash/uniq';
import pull from 'lodash/pull';
import noop from 'lodash/noop';
import map from 'lodash/map';
import isObject from 'lodash/isObject';
import isEnumEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import { getProPrefixCls } from 'choerodon-ui/lib/configure/utils';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import Icon from 'choerodon-ui/lib/icon';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { Action } from 'choerodon-ui/lib/trigger/enum';
import DataSet from '../../data-set/DataSet';
import { DataSetSelection, FieldIgnore, FieldType, RecordStatus } from '../../data-set/enum';
import Button from '../../button';
import Dropdown from '../../dropdown';
import TextField from '../../text-field';
import Tooltip from '../../tooltip';
import TableContext from '../TableContext';
import { $l } from '../../locale-context';
import autobind from '../../_util/autobind';
import isEmpty from '../../_util/isEmpty';
import FieldList from '../../table/query-bar/FieldList';
import ColumnFilter from '../tool-bar/ColumnFilter';
import { ValueChangeAction } from '../../text-field/enum';
import { iteratorFilterToArray } from '../../_util/iteratorUtils';
import QuickFilterMenu from '../../table/query-bar/quick-filter/QuickFilterMenu';
import QuickFilterMenuContext from '../../table/query-bar/quick-filter/QuickFilterMenuContext';
import { ConditionDataSet, QuickFilterDataSet } from '../../table/query-bar/quick-filter/QuickFilterDataSet';
/**
 * 当前数据是否有值并需要选中
 * @param data
 */
function isSelect(data) {
    if (isObject(data[1])) {
        return !isEnumEmpty(data[1]);
    }
    return data[0] !== '__dirty' && !isEmpty(data[1]);
}
function isEqualDynamicProps(originalValue, newValue) {
    if (isEqual(newValue, originalValue)) {
        return true;
    }
    if (isObject(newValue) && isObject(originalValue) && Object.keys(newValue).length) {
        return Object.keys(newValue).every(key => {
            const value = newValue[key];
            const oldValue = originalValue[key];
            if (oldValue === value) {
                return true;
            }
            const oEp = isNumber(oldValue) ? isEmpty(oldValue) : isEnumEmpty(oldValue);
            const nEp = isNumber(value) ? isEmpty(value) : isEnumEmpty(value);
            if (oEp && nEp) {
                return true;
            }
            return isEqual(oldValue, value);
        });
    }
    return isEqual(newValue, originalValue);
}
function getQueryData(queryDataSet) {
    const queryData = omit(queryDataSet?.current?.toData(true), ['__dirty']);
    return Object.keys(queryData).reduce((p, key) => {
        const value = queryData[key];
        if (!isEmpty(value)) {
            p[key] = value;
        }
        return p;
    }, {});
}
let TableDynamicFilterBar = class TableDynamicFilterBar extends Component {
    constructor(props, context) {
        super(props, context);
        this.refDropdown = null;
        this.refFilterWrapper = null;
        this.refSingleWrapper = null;
        this.refEditors = new Map();
        this.originalConditionFields = [];
        this.enterNum = 0;
        this.handleClickOut = (e) => {
            if (this.refDropdown && !this.refDropdown.contains(e.target)) {
                this.fieldSelectHidden = true;
            }
        };
        this.setOriginalConditionFields = (code) => {
            const { queryDataSet } = this.props;
            if (!code) {
                this.originalConditionFields = [];
            }
            else {
                const codes = Array.isArray(code) ? code : [code];
                this.originalConditionFields = uniq([...this.originalConditionFields, ...codes]);
            }
            queryDataSet.setState('selectFields', [...this.originalConditionFields]);
        };
        /**
         * 勾选
         * @param code
         */
        this.handleSelect = (code) => {
            const { queryDataSet } = this.props;
            const codes = Array.isArray(code) ? code : [code];
            const selectFields = queryDataSet.getState('selectFields') || [];
            queryDataSet.setState('selectFields', uniq([...selectFields, ...codes]));
            const shouldUpdate = !isEqual(toJS(queryDataSet.getState('selectFields') || []), toJS(this.originalConditionFields));
            this.setConditionStatus(shouldUpdate ? RecordStatus.update : RecordStatus.sync);
        };
        /**
         * 取消勾选
         * @param code
         */
        this.handleUnSelect = (code) => {
            const { queryDataSet } = this.props;
            const codes = Array.isArray(code) ? code : [code];
            if (queryDataSet) {
                const { current } = queryDataSet;
                if (current) {
                    codes.forEach((name) => current.set(name, undefined));
                }
            }
            const selectFields = queryDataSet.getState('selectFields') || [];
            queryDataSet.setState('selectFields', pull([...selectFields], ...codes));
            const shouldUpdate = !isEqual(toJS(queryDataSet.getState('selectFields')), toJS(this.originalConditionFields));
            this.setConditionStatus(shouldUpdate ? RecordStatus.update : RecordStatus.sync);
        };
        runInAction(() => {
            this.fieldSelectHidden = true;
            this.expand = true;
        });
    }
    static get contextType() {
        return TableContext;
    }
    componentDidMount() {
        this.processDataSetListener(true);
        document.addEventListener('click', this.handleClickOut);
    }
    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOut);
        this.processDataSetListener(false);
    }
    processDataSetListener(flag) {
        const { queryDataSet } = this.props;
        if (queryDataSet) {
            const handler = flag ? queryDataSet.addEventListener : queryDataSet.removeEventListener;
            handler.call(queryDataSet, 'validate', this.handleDataSetValidate);
            handler.call(queryDataSet, 'update', this.handleDataSetUpdate);
            handler.call(queryDataSet, 'create', this.handleDataSetCreate);
        }
    }
    /**
     * queryDataSet 查询前校验事件 触发展开动态字段
     * @param dataSet 查询DS
     * @param result
     */
    async handleDataSetValidate({ dataSet, result }) {
        if (!await result) {
            runInAction(() => {
                const { current } = dataSet;
                dataSet.fields.forEach((field, key) => {
                    if (!field.isValid(current)) {
                        this.handleSelect(key);
                    }
                });
                this.expand = true;
            });
            const { refSingleWrapper } = this;
            if (refSingleWrapper) {
                refSingleWrapper.style.height = '';
                refSingleWrapper.style.overflow = '';
            }
        }
    }
    setConditionStatus(value, orglValue) {
        const { queryDataSet } = this.props;
        queryDataSet.setState('conditionStatus', value);
        if (value === RecordStatus.sync && orglValue) {
            this.originalValue = orglValue;
        }
    }
    /**
     * 筛选条件更新 触发表格查询
     */
    handleDataSetUpdate({ record }) {
        const { dataSet, onQuery = noop, autoQuery } = this.props;
        let status = RecordStatus.update;
        if (record) {
            status = isEqualDynamicProps(this.originalValue, record.toData()) ? RecordStatus.sync : RecordStatus.update;
        }
        this.setConditionStatus(status);
        if (autoQuery) {
            dataSet.query();
            onQuery();
        }
    }
    /**
     * queryDS 新建，初始勾选值
     */
    handleDataSetCreate(props) {
        const { dataSet, record } = props;
        const { queryDataSet } = this.props;
        const originalValue = record.toData();
        const conditionData = Object.entries(originalValue);
        this.originalValue = originalValue;
        const { fields } = dataSet;
        map(conditionData, data => {
            let name = data[0];
            if (!fields.has(data[0]) &&
                isObject(data[1]) &&
                !isEnumEmpty(data[1]) &&
                !isArray(data[1])) {
                name = `${data[0]}.${Object.keys(data[1])[0]}`;
            }
            if (isSelect(data) && !(queryDataSet.getState('selectFields') || []).includes(name)) {
                const field = dataSet.getField(name);
                if (!field || !field.get('bind', record)) {
                    this.originalConditionFields.push(name);
                    this.handleSelect(name);
                }
            }
        });
        this.initMenuDataSet();
    }
    async initMenuDataSet() {
        const { queryDataSet, dataSet, dynamicFilterBar, searchCode } = this.props;
        const searchCodes = dynamicFilterBar && dynamicFilterBar.searchCode || searchCode;
        const menuDataSet = new DataSet(QuickFilterDataSet({
            searchCode: searchCodes,
            queryDataSet,
            tableFilterAdapter: this.tableFilterAdapter,
        }));
        const conditionDataSet = new DataSet(ConditionDataSet());
        const optionDataSet = new DataSet({
            selection: DataSetSelection.single,
        });
        const filterMenuDataSet = new DataSet({
            autoCreate: true,
            fields: [
                {
                    name: 'filterName',
                    type: FieldType.string,
                    textField: 'searchName',
                    valueField: 'searchId',
                    options: optionDataSet,
                    ignore: FieldIgnore.always,
                },
            ],
        });
        queryDataSet.setState('menuDataSet', menuDataSet);
        queryDataSet.setState('conditionDataSet', conditionDataSet);
        queryDataSet.setState('optionDataSet', optionDataSet);
        queryDataSet.setState('filterMenuDataSet', filterMenuDataSet);
        const result = await menuDataSet.query();
        if (optionDataSet) {
            optionDataSet.loadData(result);
        }
        const menuRecord = menuDataSet.current;
        if (menuRecord) {
            conditionDataSet.loadData(menuRecord.get('conditionList'));
        }
        if (result && result.length) {
            runInAction(() => {
                this.shouldLocateData = true;
            });
        }
        else {
            const { current } = filterMenuDataSet;
            if (current)
                current.set('filterName', undefined);
            runInAction(() => {
                this.shouldLocateData = true;
            });
            if (dataSet.props.autoQuery) {
                dataSet.query();
            }
        }
    }
    /**
     * tableFilterSuffix 预留自定义区域
     */
    renderSuffix() {
        const { tableStore, tableStore: { proPrefixCls } } = this.context;
        const { dynamicFilterBar, queryDataSet, dataSet } = this.props;
        const suffixes = dynamicFilterBar && dynamicFilterBar.suffixes || tableStore.getConfig('tableFilterSuffix');
        const children = [];
        if (suffixes && suffixes.length) {
            suffixes.forEach(suffix => {
                if (suffix === 'filter') {
                    children.push(React.createElement(ColumnFilter, { prefixCls: proPrefixCls }));
                }
                else if (isValidElement(suffix)) {
                    children.push(suffix);
                }
                else if (isFunction(suffix)) {
                    children.push(suffix({ queryDataSet, dataSet }));
                }
            });
            return (React.createElement("div", { className: `${proPrefixCls}-dynamic-filter-bar-suffix` }, children));
        }
    }
    getPrefix() {
        const { tableStore: { proPrefixCls } } = this.context;
        const { dynamicFilterBar, queryDataSet, dataSet } = this.props;
        const prefixes = dynamicFilterBar && dynamicFilterBar.prefixes;
        const children = [];
        if (prefixes && prefixes.length) {
            prefixes.forEach((prefix) => {
                if (isString(prefix) && prefix === 'filter') {
                    children.push(React.createElement(ColumnFilter, { prefixCls: proPrefixCls }));
                }
                else if (isValidElement(prefix)) {
                    children.push(prefix);
                }
                else if (isFunction(prefix)) {
                    children.push(prefix({ queryDataSet, dataSet }));
                }
            });
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: `${proPrefixCls}-dynamic-filter-bar-prefix` }, children),
                React.createElement("span", { className: `${proPrefixCls}-filter-search-divide` })));
        }
        return null;
    }
    /**
     * 注入 onEnterDown 事件
     * @param element
     * @param name
     */
    createFields(element, name) {
        const { onEnterDown } = element.props;
        if (onEnterDown && isFunction(onEnterDown)) {
            return element;
        }
        const props = {
            onEnterDown: () => {
                this.handleQuery();
            },
            ref: (node) => this.refEditors.set(name, node),
        };
        return cloneElement(element, props);
    }
    /**
     * 判断查询值是否为空
     * @param value
     */
    isEmpty(value) {
        return isArrayLike(value) ? !value.length : isEmpty(value);
    }
    get tableFilterAdapter() {
        const { dynamicFilterBar } = this.props;
        const { tableStore } = this.context;
        return dynamicFilterBar && dynamicFilterBar.tableFilterAdapter || tableStore.getConfig('tableFilterAdapter');
    }
    /**
     * 是否单行操作
     */
    isSingleLineOpt() {
        const { fuzzyQuery } = this.props;
        return !(fuzzyQuery || this.tableFilterAdapter);
    }
    /**
     * 渲染展开逻辑
     * @param hidden 是否隐藏全部
     */
    getExpandNode(hidden) {
        const { tableStore: { proPrefixCls } } = this.context;
        const { expandButton } = this.props;
        if (!expandButton) {
            return;
        }
        return (React.createElement("span", { className: `${proPrefixCls}-filter-menu-expand`, onClick: () => {
                const { refSingleWrapper } = this;
                if (refSingleWrapper) {
                    const { height } = refSingleWrapper.getBoundingClientRect();
                    const { height: childHeight } = refSingleWrapper.children[0].children[0].getBoundingClientRect();
                    runInAction(() => {
                        this.expand = hidden ? height <= 0 : height <= (childHeight + 18);
                    });
                    if (hidden && height) {
                        // 收起全部
                        refSingleWrapper.style.display = 'none';
                    }
                    else {
                        refSingleWrapper.style.display = 'flex';
                        refSingleWrapper.style.height = '';
                        refSingleWrapper.style.overflow = '';
                    }
                    if (height > (childHeight + 18) && !hidden) {
                        // 收起留一行高度
                        refSingleWrapper.style.height = pxToRem(childHeight + 18) || '';
                        refSingleWrapper.style.overflow = 'hidden';
                    }
                    else {
                        refSingleWrapper.style.height = '';
                        refSingleWrapper.style.overflow = '';
                    }
                }
            } }, this.expand ? (React.createElement(Tooltip, { title: $l('Table', 'collapse') },
            React.createElement(Icon, { type: "baseline-arrow_drop_up" }))) : (React.createElement(Tooltip, { title: $l('Table', 'expand_button') },
            React.createElement(Icon, { type: "baseline-arrow_drop_down" })))));
    }
    /**
     * 渲染模糊搜索
     */
    getFuzzyQuery() {
        const { tableStore, tableStore: { proPrefixCls, node, highlightRowIndexs } } = this.context;
        const { dataSet, dynamicFilterBar, autoQueryAfterReset, fuzzyQuery, fuzzyQueryPlaceholder, onReset = noop } = this.props;
        const searchText = dynamicFilterBar?.searchText || tableStore.getConfig('tableFilterSearchText') || 'params';
        const placeholder = fuzzyQueryPlaceholder || $l('Table', 'enter_search_content');
        if (!fuzzyQuery)
            return null;
        return (React.createElement("div", { className: `${proPrefixCls}-filter-search` },
            React.createElement(TextField, { style: { width: 280 }, clearButton: true, placeholder: placeholder, prefix: React.createElement(Icon, { type: "search" }), value: this.searchText, valueChangeAction: ValueChangeAction.input, onChange: debounce((value) => {
                    runInAction(() => {
                        this.searchText = value || '';
                        if (dynamicFilterBar?.quickSearch) {
                            tableStore.highlightRowIndexs = [];
                            tableStore.searchText = value || '';
                            node.forceUpdate();
                        }
                    });
                    dataSet.setQueryParameter(searchText, value);
                    this.handleQuery();
                }, 500), onKeyDown: (e) => {
                    if (e.keyCode === KeyCode.ENTER && dynamicFilterBar?.quickSearch) {
                        e.preventDefault();
                        const rows = [...new Set(highlightRowIndexs)];
                        node.scrollTop(rows[this.enterNum] * node.getRowHeight());
                        this.enterNum += 1;
                        if (this.enterNum + 1 > rows.length) {
                            this.enterNum = 0;
                        }
                    }
                }, onClear: () => {
                    runInAction(() => {
                        this.searchText = '';
                        if (dynamicFilterBar?.quickSearch) {
                            tableStore.searchText = '';
                            tableStore.highlightRowIndexs = [];
                            node.forceUpdate();
                        }
                    });
                    onReset();
                    if (autoQueryAfterReset) {
                        dataSet.setQueryParameter(searchText, undefined);
                        this.handleQuery(true);
                    }
                } })));
    }
    /**
     * 渲染重置按钮
     */
    getResetButton() {
        const { tableStore: { proPrefixCls } } = this.context;
        const { queryDataSet, autoQueryAfterReset, onReset = noop } = this.props;
        return (React.createElement("div", { className: `${proPrefixCls}-filter-buttons` }, queryDataSet.getState('conditionStatus') === RecordStatus.update && (React.createElement(Button, { onClick: () => {
                if (queryDataSet) {
                    const { current } = queryDataSet;
                    if (current) {
                        current.reset();
                        this.handleDataSetCreate({ dataSet: queryDataSet, record: current });
                    }
                }
                this.setConditionStatus(RecordStatus.sync);
                onReset();
                if (autoQueryAfterReset) {
                    this.handleQuery();
                }
            } }, $l('Table', 'reset_button')))));
    }
    /**
     * 筛选头
     * fuzzyQuery + quickFilterMenu + resetButton + buttons
     */
    getFilterMenu() {
        const { tableStore: { proPrefixCls } } = this.context;
        const { queryFields, queryDataSet, dataSet, dynamicFilterBar, searchCode, autoQuery, expandButton } = this.props;
        if (queryDataSet && queryFields.length) {
            const prefix = this.getPrefix();
            const fuzzyQuery = this.getFuzzyQuery();
            const searchCodes = dynamicFilterBar && dynamicFilterBar.searchCode || searchCode;
            const quickFilterMenu = this.tableFilterAdapter && searchCodes ? (React.createElement(QuickFilterMenuContext.Provider, { value: {
                    autoQuery,
                    prefixCls: proPrefixCls,
                    expand: this.expand,
                    dataSet,
                    queryDataSet,
                    onChange: this.handleSelect,
                    conditionStatus: queryDataSet.getState('conditionStatus'),
                    onStatusChange: this.setConditionStatus,
                    selectFields: queryDataSet.getState('selectFields'),
                    onOriginalChange: this.setOriginalConditionFields,
                    menuDataSet: queryDataSet.getState('menuDataSet'),
                    filterMenuDataSet: queryDataSet.getState('filterMenuDataSet'),
                    conditionDataSet: queryDataSet.getState('conditionDataSet'),
                    optionDataSet: queryDataSet.getState('optionDataSet'),
                    shouldLocateData: this.shouldLocateData,
                } },
                React.createElement(QuickFilterMenu, null))) : null;
            const resetButton = this.isSingleLineOpt() || this.tableFilterAdapter ? null : this.getResetButton();
            const expandNode = this.getExpandNode(true);
            return (React.createElement("div", { className: `${proPrefixCls}-filter-menu` },
                prefix,
                fuzzyQuery,
                quickFilterMenu,
                resetButton,
                this.isSingleLineOpt() ? null : (React.createElement(React.Fragment, null,
                    expandButton ? React.createElement("span", { className: `${proPrefixCls}-filter-search-divide` }) : null,
                    expandNode)),
                this.renderSuffix()));
        }
    }
    /**
     * 渲染查询条
     */
    getQueryBar() {
        const { tableStore: { proPrefixCls } } = this.context;
        const { queryFieldsLimit = 3, queryFields, queryDataSet } = this.props;
        const selectFields = queryDataSet.getState('selectFields') || [];
        const singleLineModeAction = this.isSingleLineOpt() ?
            React.createElement("div", { className: `${proPrefixCls}-dynamic-filter-bar-single-action` },
                this.getResetButton(),
                this.getExpandNode(false)) : null;
        if (queryDataSet && queryFields.length) {
            return (React.createElement("div", { key: "query_bar", className: `${proPrefixCls}-dynamic-filter-bar` },
                this.getFilterMenu(),
                React.createElement("div", { className: `${proPrefixCls}--dynamic-filter-single-wrapper`, ref: (node) => this.refSingleWrapper = node },
                    React.createElement("div", { className: `${proPrefixCls}-filter-wrapper` },
                        queryFields.slice(0, queryFieldsLimit).map(element => {
                            const { name, hidden } = element.props;
                            if (hidden)
                                return null;
                            const queryField = queryDataSet.getField(name);
                            const itemClassName = `${proPrefixCls}-filter-item`;
                            return (React.createElement("div", { className: `${proPrefixCls}-filter-content`, key: name, onClick: () => this.refEditors.get(name).focus() },
                                React.createElement("span", { className: `${proPrefixCls}-filter-label` }, queryField?.get('label')),
                                React.createElement("span", { className: itemClassName }, this.createFields(element, name))));
                        }),
                        queryFields.slice(queryFieldsLimit).map(element => {
                            const { name, hidden } = element.props;
                            if (hidden)
                                return null;
                            const queryField = queryDataSet.getField(name);
                            if (selectFields.includes(name)) {
                                return (React.createElement("div", { className: `${proPrefixCls}-filter-content`, key: name, onClick: () => this.refEditors.get(name).focus() },
                                    React.createElement(Icon, { type: "cancel", className: `${proPrefixCls}-filter-item-close`, onClick: () => {
                                            this.handleUnSelect([name]);
                                        } }),
                                    React.createElement("span", { className: `${proPrefixCls}-filter-label` }, queryField?.get('label')),
                                    React.createElement("span", { className: `${proPrefixCls}-filter-item` }, this.createFields(element, name))));
                            }
                            return null;
                        }),
                        (queryFieldsLimit < queryFields.length) && (React.createElement("div", { className: `${proPrefixCls}-filter-item` },
                            React.createElement(Dropdown, { visible: !this.fieldSelectHidden, overlay: (React.createElement("div", { role: "none", ref: (node) => this.refDropdown = node, onClick: (e) => {
                                        e.stopPropagation();
                                    } },
                                    React.createElement(FieldList, { groups: [{
                                                title: $l('Table', 'predefined_fields'),
                                                fields: iteratorFilterToArray(queryDataSet.fields.values(), f => !f.get('bind') && !f.get('name').includes('__tls')).slice(queryFieldsLimit),
                                            }], prefixCls: `${proPrefixCls}-filter-list` || 'c7n-pro-table-filter-list', closeMenu: () => runInAction(() => this.fieldSelectHidden = true), value: selectFields, onSelect: this.handleSelect, onUnSelect: this.handleUnSelect }))), trigger: [Action.click] },
                                React.createElement("span", { className: `${proPrefixCls}-add-fields`, onClick: (e) => {
                                        e.nativeEvent.stopImmediatePropagation();
                                        runInAction(() => {
                                            this.fieldSelectHidden = false;
                                        });
                                    } },
                                    React.createElement(Icon, { type: "add" }),
                                    $l('Table', 'add_filter')))))),
                    singleLineModeAction)));
        }
        return null;
    }
    handleQuery(collapse) {
        const { dataSet, queryDataSet, onQuery = noop, autoQuery } = this.props;
        if (autoQuery) {
            dataSet.query();
        }
        if (!collapse) {
            const data = getQueryData(queryDataSet);
            onQuery({ dataSet, queryDataSet, data, params: this.searchText });
        }
    }
    render() {
        return this.getQueryBar();
    }
};
TableDynamicFilterBar.defaultProps = {
    prefixCls: getProPrefixCls('table'),
    queryFieldsLimit: 3,
    autoQueryAfterReset: true,
    fuzzyQuery: true,
    autoQuery: true,
    expandButton: true,
};
__decorate([
    observable
], TableDynamicFilterBar.prototype, "moreFields", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "fieldSelectHidden", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "expand", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "searchText", void 0);
__decorate([
    observable
], TableDynamicFilterBar.prototype, "shouldLocateData", void 0);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleClickOut", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetValidate", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "setConditionStatus", null);
__decorate([
    action
], TableDynamicFilterBar.prototype, "setOriginalConditionFields", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetUpdate", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleDataSetCreate", null);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "initMenuDataSet", null);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleSelect", void 0);
__decorate([
    action
], TableDynamicFilterBar.prototype, "handleUnSelect", void 0);
__decorate([
    autobind
], TableDynamicFilterBar.prototype, "handleQuery", null);
TableDynamicFilterBar = __decorate([
    observer
], TableDynamicFilterBar);
export default TableDynamicFilterBar;
